/**
 * Wings: switches views inside the mobile menu sheet (snippets/wings-mobile-menu.liquid).
 * Opening a department eases the main view out to one side and the department view in
 * from the other, in the same sheet; Back reverses it. Inactive views are inert.
 * The menu returns to the main view whenever the drawer closes.
 */
/** The sheet fades in place below this width (snippets/wings-mobile-menu.liquid) */
const PHONE = window.matchMedia('(max-width: 749px)');

class WingsMobileMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.#details = this.closest('details');
    this.#details?.addEventListener('toggle', this.#onToggle, { signal });
    // Capture runs before the summary's own toggle, so "was it closed" is still readable
    this.#details?.addEventListener('click', this.#onSummaryClick, { signal, capture: true });
    // a phone turned while the menu is open: the menu icon has moved
    window.addEventListener('resize', () => this.#details?.open && this.#alignCloseButton(), { signal });
  }

  /** @param {MouseEvent} event */
  #onSummaryClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest('summary');
    if (!summary || summary.parentElement !== this.#details) return;
    // measured once the sheet is in its open position, before that frame is painted (on phones the
    // sheet fades in place; wider, it slides in, and its X stays where Atelier puts it)
    if (!this.#details.open) requestAnimationFrame(() => this.#alignCloseButton());
  };

  /**
   * The sheet's X sits exactly where the menu icon is, so opening reads as the icon turning
   * into an X in place (the header's position depends on the announcement bar, so it's measured).
   */
  #alignCloseButton() {
    const menuIcon = this.#details?.querySelector(':scope > summary .header-drawer-icon--open');
    const close = this.#details?.querySelector('.menu-drawer__close-button');
    const closeIcon = close?.querySelector('.header-drawer-icon');
    if (!(close instanceof HTMLElement) || !menuIcon || !closeIcon) return;

    close.style.translate = '';
    this.style.removeProperty('--wmm-close-shift');
    if (!PHONE.matches) return;
    const from = closeIcon.getBoundingClientRect();
    const to = menuIcon.getBoundingClientRect();
    if (!to.width || !from.width) return;

    const x = to.left + to.width / 2 - (from.left + from.width / 2);
    const y = to.top + to.height / 2 - (from.top + from.height / 2);
    close.style.translate = `${x}px ${y}px`;
    // the menu below keeps its distance from the X
    this.style.setProperty('--wmm-close-shift', `${Math.max(0, y)}px`);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-wmm-open]');
    if (opener instanceof HTMLElement && opener.dataset.wmmOpen) {
      this.show(opener.dataset.wmmOpen, opener);
      return;
    }

    if (event.target.closest('[data-wmm-back]')) this.back();
    // detail 0 = activated from the keyboard (Enter/Space), where the returning focus ring belongs
    if (event.target.closest('.wmm__search')) this.#focusSearchWithinTap(event.detail === 0);
  };

  /**
   * The search panel opens a frame after the tap, and iPhones only raise the keyboard for a
   * field focused within the tap itself. A stand-in field takes focus now (keyboard up), then
   * hands it to the panel's search field once the panel is open. Closing search returns focus
   * to the stand-in (the panel remembers it), so it stays, as a plain button, until then and
   * passes focus back to the menu's Search line.
   */
  /** @param {boolean} fromKeyboard */
  #focusSearchWithinTap(fromKeyboard) {
    const input = document.querySelector('#search-modal input[type="search"]');
    const dialog = input?.closest('dialog');
    if (!(input instanceof HTMLInputElement) || !dialog) return;

    const standIn = document.createElement('input');
    standIn.type = 'text';
    standIn.tabIndex = -1;
    // focused for a moment, so it carries a name rather than being hidden from screen readers
    standIn.setAttribute('aria-label', input.getAttribute('aria-label') || 'Search');
    // 16px so iPhones don't zoom; inside the menu so its focus trap leaves it alone
    standIn.style.cssText = 'position:fixed;inset-block-start:0;inset-inline-start:0;width:1px;height:1px;opacity:0;font-size:16px;pointer-events:none;';
    this.append(standIn);
    standIn.focus({ preventScroll: true });

    const searchLine = this.querySelector('.wmm__search');
    let frames = 0;
    const handOver = () => {
      if (!dialog.open && frames++ < 30) return requestAnimationFrame(handOver);
      if (!dialog.open) return standIn.remove();
      input.focus({ preventScroll: true });
      // focus coming back to it must not raise the keyboard
      standIn.type = 'button';
      dialog.addEventListener(
        'close',
        () => {
          if (this.#details?.open && searchLine instanceof HTMLElement) {
            // after a tap, back on the Search line without a ring (iPhones ring script focus)
            if (!fromKeyboard) {
              searchLine.setAttribute('data-quiet-focus', '');
              searchLine.addEventListener('blur', () => searchLine.removeAttribute('data-quiet-focus'), { once: true });
            }
            searchLine.focus({ preventScroll: true, focusVisible: fromKeyboard });
          }
          standIn.remove();
        },
        { once: true }
      );
    };
    requestAnimationFrame(handOver);
  }

  #onToggle = () => {
    if (this.#details?.open) return;
    // Wait for the drawer to finish closing before resetting, so the change isn't seen.
    setTimeout(() => this.reset(), 350);
  };

  /**
   * @param {string} id - The view to show
   * @param {HTMLElement} [opener] - The row that opened it, for focus on Back
   */
  show(id, opener) {
    const root = this.#view('root');
    const view = this.#view(id);
    if (!root || !view) return;

    this.#opener = opener ?? null;
    this.#set(root, 'left');
    this.#set(view, 'active');
    this.closest('.menu-drawer')?.scrollTo({ top: 0 });

    const back = view.querySelector('[data-wmm-back]');
    if (back instanceof HTMLElement) this.#focusAfterTransition(view, back);
  }

  back() {
    const root = this.#view('root');
    const active = this.querySelector('[data-wmm-view][data-state="active"]');
    if (!root || !(active instanceof HTMLElement) || active === root) return;

    this.#set(active, 'right');
    this.#set(root, 'active');
    if (this.#opener) this.#focusAfterTransition(root, this.#opener);
  }

  reset() {
    for (const view of this.querySelectorAll('[data-wmm-view]')) {
      if (view instanceof HTMLElement) this.#set(view, view.dataset.wmmView === 'root' ? 'active' : 'right');
    }
  }

  /**
   * Move focus once the view has finished becoming visible (focus fails while its
   * visibility is still transitioning). Falls back to a timer if no transition runs.
   * @param {HTMLElement} view
   * @param {HTMLElement} target
   */
  #focusAfterTransition(view, target) {
    let done = false;
    const focus = () => {
      if (done) return;
      done = true;
      target.focus({ preventScroll: true });
    };
    view.addEventListener('transitionend', (event) => event.propertyName === 'opacity' && focus());
    setTimeout(focus, 450);
  }

  /** @param {string} id */
  #view(id) {
    const view = this.querySelector(`[data-wmm-view="${id}"]`);
    return view instanceof HTMLElement ? view : null;
  }

  /**
   * @param {HTMLElement} view
   * @param {'active' | 'left' | 'right'} state
   */
  #set(view, state) {
    view.dataset.state = state;
    view.inert = state !== 'active';
  }
}

if (!customElements.get('wings-mobile-menu')) {
  customElements.define('wings-mobile-menu', WingsMobileMenu);
}
